import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Play, Download, RefreshCw, CheckCircle, XCircle, Clock, Users, AlertCircle, MessageCircle, Bot, Send, Database } from 'lucide-react';
import { cognitoAuth } from '@/lib/cognito';
import { apiClient, API_BASE_URL } from '@/lib/api';
import { useAuth } from '@/hooks/useApi';
import { useTenantContext } from '@/contexts/TenantContext';
import SampleDataGenerator from './SampleDataGenerator';

interface TestResult {
  test_type: string;
  script: string;
  exit_code: number;
  stdout: string;
  stderr: string;
  success: boolean;
  timestamp?: number;
}

interface TestLogs {
  [filename: string]: string;
}

interface AutomatedTestResult {
  timestamp: string;
  environment: string;
  test_type: string;
  total_tests: number;
  passed: number;
  failed: number;
  duration_seconds: number;
  results: any[];
}

export function SystemTests() {
  const { isSuperAdmin, user } = useAuth();
  const { selectedTenantId } = useTenantContext();
  const activeTenantId = isSuperAdmin ? selectedTenantId : user?.tenant_from_group;
  
  const [isRunning, setIsRunning] = useState(false);
  const [testResults, setTestResults] = useState<TestResult | null>(null);
  const [logs, setLogs] = useState<TestLogs>({});
  const [selectedLogFile, setSelectedLogFile] = useState<string>('');
  const [cloudWatchMinutes, setCloudWatchMinutes] = useState(10);
  const [cloudWatchLimit, setCloudWatchLimit] = useState(200);
  const [fileLogMinutes, setFileLogMinutes] = useState(1440); // 24 hours default
  const [fileLogLimit, setFileLogLimit] = useState(20);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  
  // Test Queue Entries state
  const [queues, setQueues] = useState<any[]>([]);
  const [selectedQueue, setSelectedQueue] = useState<string>('');
  const [queueConfig, setQueueConfig] = useState<any>(null);
  const [testCount, setTestCount] = useState(5);
  const [isAddingEntries, setIsAddingEntries] = useState(false);
  const [entryLog, setEntryLog] = useState<string>('');
  
  // SMS Chat Testing state
  const [smsMessages, setSmsMessages] = useState<Array<{id: string, direction: 'in' | 'out', message: string, timestamp: Date, phone: string}>>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [testPhone, setTestPhone] = useState('+18014723959');
  const [isSendingSms, setIsSendingSms] = useState(false);
  
  // AI Testing state
  const [aiTestResults, setAiTestResults] = useState<any>(null);
  const [isTestingAi, setIsTestingAi] = useState(false);
  
  // Automated Tests state
  const [automatedTestHistory, setAutomatedTestHistory] = useState<AutomatedTestResult[]>([]);
  const [selectedTestResult, setSelectedTestResult] = useState<AutomatedTestResult | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isRunningAutomatedTest, setIsRunningAutomatedTest] = useState(false);

  const runTests = async (testType: 'all' | 'auth' | 'tenant' | 'simple' | 'queue' | 'reports' | 'lex') => {
    console.log(`=== STARTING TEST: ${testType} ===`);
    setIsRunning(true);
    setTestResults(null);
    
    try {
      const token = cognitoAuth.getIdToken();
      console.log('Token exists:', !!token);
      console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'null');
      console.log('Is authenticated:', cognitoAuth.isAuthenticated());
      
      const requestBody = { test_type: testType };
      console.log('Request body:', requestBody);
      
      console.log('Making fetch request to:', `${API_BASE_URL}/admin/tests/run`);
      
      const response = await fetch(`${API_BASE_URL}/admin/tests/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response not ok. Status:', response.status, 'Text:', errorText);
        
        setTestResults({
          test_type: testType,
          script: 'unknown',
          exit_code: response.status,
          stdout: '',
          stderr: `HTTP ${response.status}: ${errorText}`,
          success: false
        });
        return;
      }
      
      const result = await response.json();
      console.log('Response result:', result);
      setTestResults(result);
      
    } catch (error) {
      console.error('=== FETCH ERROR ===');
      console.error('Error type:', typeof error);
      console.error('Error name:', error instanceof Error ? error.name : 'unknown');
      console.error('Error message:', error instanceof Error ? error.message : String(error));
      console.error('Full error:', error);
      
      setTestResults({
        test_type: testType,
        script: 'unknown',
        exit_code: -1,
        stdout: '',
        stderr: `Network/Fetch error: ${error instanceof Error ? error.message : String(error)}`,
        success: false
      });
    } finally {
      console.log('=== TEST COMPLETED ===');
      setIsRunning(false);
    }
  };

  const loadLogs = async () => {
    try {
      const url = `${API_BASE_URL}/admin/tests/logs?minutes=${fileLogMinutes}&limit=${fileLogLimit}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${cognitoAuth.getIdToken()}`
        }
      });
      
      const result = await response.json();
      setLogs(result.logs || {});
      
      // Auto-select first log file if none selected
      if (!selectedLogFile && result.available_files?.length > 0) {
        setSelectedLogFile(result.available_files[0]);
      }
    } catch (error) {
      console.error('Failed to load logs:', error);
    }
  };

  const cleanupLogs = async (daysOld: number) => {
    setIsCleaningUp(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/tests/cleanup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${cognitoAuth.getIdToken()}`
        },
        body: JSON.stringify({ days_old: daysOld })
      });
      
      const result = await response.json();
      
      if (result.cleanup_completed) {
        alert(`Cleanup completed!\n\nFiles deleted: ${result.files_deleted}\nTotal files found: ${result.total_files_found}\nThreshold: ${daysOld} days old`);
        // Refresh logs after cleanup
        loadLogs();
      } else {
        alert(`Cleanup failed: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to cleanup logs:', error);
      alert(`Cleanup failed: ${error}`);
    } finally {
      setIsCleaningUp(false);
    }
  };

  const loadCloudWatchLogs = async () => {
    try {
      const url = `${API_BASE_URL}/admin/tests/cloudwatch?minutes=${cloudWatchMinutes}&limit=${cloudWatchLimit}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${cognitoAuth.getIdToken()}`
        }
      });
      
      const result = await response.json();
      
      if (result.events && result.events.length > 0) {
        const logContent = result.events
          .map((event: any) => `${new Date(event.timestamp).toISOString()} ${event.message}`)
          .join('\n');
        
        const fileName = `cloudwatch_logs_${cloudWatchMinutes}min.txt`;
        setLogs(prev => ({
          ...prev,
          [fileName]: logContent
        }));
        
        setSelectedLogFile(fileName);
      } else {
        const fileName = `cloudwatch_logs_${cloudWatchMinutes}min.txt`;
        setLogs(prev => ({
          ...prev,
          [fileName]: `No logs found for the last ${cloudWatchMinutes} minutes.\n\nTry:\n- Running a test first\n- Increasing the time range\n- Checking if you have super admin permissions`
        }));
        setSelectedLogFile(fileName);
      }
    } catch (error) {
      console.error('Failed to load CloudWatch logs:', error);
      const fileName = `cloudwatch_error.txt`;
      setLogs(prev => ({
        ...prev,
        [fileName]: `Error loading CloudWatch logs: ${error}`
      }));
      setSelectedLogFile(fileName);
    }
  };

  const fetchQueues = async () => {
    if (!activeTenantId) {
      console.log('No activeTenantId, skipping queue fetch');
      return;
    }
    console.log('Fetching queues for tenant:', activeTenantId);
    try {
      const response = await apiClient.getQueuesSummary(activeTenantId);
      console.log('Queue response:', response);
      const activeQueues = (response.queues || []).filter((q: any) => !q.archived);
      console.log('Active queues:', activeQueues);
      setQueues(activeQueues);
    } catch (err) {
      console.error('Failed to fetch queues:', err);
    }
  };

  const fetchQueueConfig = async (tenantId: string, locationId: string, serviceId: string) => {
    try {
      const config = await apiClient.getQueueConfig(tenantId, locationId, serviceId);
      setQueueConfig(config);
    } catch (err) {
      console.error('Failed to fetch queue config:', err);
      setQueueConfig(null);
    }
  };

  const generateRandomAnswer = (question: any) => {
    if (question.type === 'select' && question.options) {
      return question.options[Math.floor(Math.random() * question.options.length)];
    }
    if (question.type === 'number') {
      const min = question.min || 1;
      const max = question.max || 10;
      return (Math.floor(Math.random() * (max - min + 1)) + min).toString();
    }
    const words = ['Test', 'Sample', 'Demo', 'Example', 'Random', 'Data', 'Entry', 'Mock'];
    return words[Math.floor(Math.random() * words.length)];
  };

  const generateRandomName = () => {
    const firstNames = ['Alex', 'Jordan', 'Taylor', 'Casey', 'Morgan', 'Riley', 'Avery', 'Quinn', 'Sage', 'River', 'Phoenix', 'Rowan', 'Skyler', 'Cameron', 'Blake', 'Drew', 'Emery', 'Finley', 'Harper', 'Hayden', 'Indigo', 'Jamie', 'Kai', 'Lane', 'Marley', 'Nova', 'Ocean', 'Parker', 'Reese', 'Sam'];
    const lastNames = ['Anderson', 'Brown', 'Davis', 'Garcia', 'Johnson', 'Jones', 'Martinez', 'Miller', 'Moore', 'Rodriguez', 'Smith', 'Taylor', 'Thomas', 'Thompson', 'White', 'Williams', 'Wilson', 'Clark', 'Lewis', 'Lee', 'Walker', 'Hall', 'Allen', 'Young', 'King', 'Wright', 'Lopez', 'Hill', 'Scott', 'Green'];
    
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    return `${firstName} ${lastName}`;
  };

  const generateRandomPhone = () => {
    const randomDigits = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `+1801555${randomDigits}`;
  };

  const addTestEntries = async () => {
    if (!selectedQueue || !activeTenantId) return;
    
    const [locationId, serviceId] = selectedQueue.split('/');
    
    setIsAddingEntries(true);
    setEntryLog('Starting to add test entries...\n');
    
    for (let i = 0; i < testCount; i++) {
      const name = generateRandomName();
      const phone = generateRandomPhone();
      
      const answers: Record<string, string> = {};
      if (queueConfig?.questions) {
        queueConfig.questions.forEach((q: any) => {
          const questionId = q.key || q.id;
          answers[questionId] = generateRandomAnswer(q);
        });
      }
      
      try {
        await apiClient.joinQueue({
          tenant_id: activeTenantId,
          location_id: locationId,
          service_id: serviceId,
          phone,
          name,
          agree_version: '1.0',
          meta: { answers }
        });
        
        setEntryLog(prev => prev + `✓ Added ${name} (${phone})\n`);
      } catch (error) {
        setEntryLog(prev => prev + `✗ Failed to add ${name}: ${error}\n`);
      }
      
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setEntryLog(prev => prev + 'Finished adding test entries!\n');
    setIsAddingEntries(false);
  };

  useEffect(() => {
    if (activeTenantId) {
      fetchQueues();
    }
  }, [activeTenantId]);

  useEffect(() => {
    if (selectedQueue && activeTenantId) {
      const [locationId, serviceId] = selectedQueue.split('/');
      fetchQueueConfig(activeTenantId, locationId, serviceId);
    }
  }, [selectedQueue, activeTenantId]);

  const sendSmsMessage = async () => {
    if (!currentMessage.trim()) return;
    
    setIsSendingSms(true);
    const messageId = Date.now().toString();
    
    // Add outgoing message to chat
    const outgoingMessage = {
      id: messageId + '_out',
      direction: 'out' as const,
      message: currentMessage,
      timestamp: new Date(),
      phone: testPhone
    };
    
    setSmsMessages(prev => [...prev, outgoingMessage]);
    const messageToSend = currentMessage;
    setCurrentMessage('');
    
    try {
      // Simulate SMS inbound by calling the SMS handler directly
      const response = await fetch(`${API_BASE_URL}/admin/tests/sms-simulate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${cognitoAuth.getIdToken()}`
        },
        body: JSON.stringify({
          phone: testPhone,
          message: messageToSend,
          tenant: activeTenantId
        })
      });
      
      const result = await response.json();
      
      // Add incoming response to chat
      if (result.response) {
        const incomingMessage = {
          id: messageId + '_in',
          direction: 'in' as const,
          message: result.response,
          timestamp: new Date(),
          phone: testPhone
        };
        setSmsMessages(prev => [...prev, incomingMessage]);
      }
      
    } catch (error) {
      console.error('SMS simulation failed:', error);
      const errorMessage = {
        id: messageId + '_error',
        direction: 'in' as const,
        message: `Error: ${error instanceof Error ? error.message : 'SMS simulation failed'}`,
        timestamp: new Date(),
        phone: testPhone
      };
      setSmsMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSendingSms(false);
    }
  };

  const testAiFeatures = async () => {
    setIsTestingAi(true);
    setAiTestResults(null);
    
    const testMessages = [
      'What is my status?',
      'How long is the wait?',
      'Push me back 10 minutes',
      'I want to leave the queue',
      'Switch to a different queue'
    ];
    
    const results = [];
    
    for (const message of testMessages) {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/tests/ai-test`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${cognitoAuth.getIdToken()}`
          },
          body: JSON.stringify({
            message,
            phone: testPhone,
            tenant: activeTenantId
          })
        });
        
        const result = await response.json();
        results.push({
          message,
          success: response.ok,
          response: result.response || result.error,
          aiProcessed: result.ai_processed,
          intent: result.intent,
          confidence: result.confidence
        });
        
      } catch (error) {
        results.push({
          message,
          success: false,
          response: `Error: ${error}`,
          aiProcessed: false
        });
      }
    }
    
    setAiTestResults(results);
    setIsTestingAi(false);
  };

  const clearSmsChat = () => {
    setSmsMessages([]);
  };
  
  const runAutomatedTest = async (testType: string) => {
    setIsRunningAutomatedTest(true);
    
    try {
      const token = cognitoAuth.getIdToken();
      const env = window.location.hostname.includes('dev') ? 'dev' : 
                  window.location.hostname.includes('test') ? 'test' : 'prod';
      
      // Trigger PowerShell script via API endpoint
      const response = await fetch(`${API_BASE_URL}/admin/test-results/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          environment: env,
          test_type: testType,
          token: token
        })
      });
      
      if (response.ok) {
        // Wait a moment for test to complete
        await new Promise(resolve => setTimeout(resolve, 3000));
        // Reload history
        await loadTestHistory();
      } else {
        alert('Failed to run test. Please try running manually from command line.');
      }
    } catch (error) {
      console.error('Failed to run automated test:', error);
      alert('Failed to run test. Please try running manually from command line.');
    } finally {
      setIsRunningAutomatedTest(false);
    }
  };
  
  const loadTestHistory = async () => {
    setIsLoadingHistory(true);
    
    try {
      const token = cognitoAuth.getIdToken();
      const env = window.location.hostname.includes('dev') ? 'dev' : 
                  window.location.hostname.includes('test') ? 'test' : 'prod';
      
      const response = await fetch(`${API_BASE_URL}/admin/test-results/list?environment=${env}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Load full content for each result
        const results: AutomatedTestResult[] = [];
        for (const item of data.results || []) {
          try {
            const contentResponse = await fetch(
              `${API_BASE_URL}/admin/test-results/result?key=${encodeURIComponent(item.key)}`,

              {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            );
            
            if (contentResponse.ok) {
              const content = await contentResponse.json();
              results.push(content);
            }
          } catch (err) {
            console.error('Failed to load test result:', err);
          }
        }
        
        // Sort by timestamp descending
        results.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setAutomatedTestHistory(results);
        
        // Auto-select most recent
        if (results.length > 0) {
          setSelectedTestResult(results[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load test history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };
  
  useEffect(() => {
    loadTestHistory();
  }, []);

  const downloadLogs = () => {
    if (!selectedLogFile || !logs[selectedLogFile]) return;
    
    const blob = new Blob([logs[selectedLogFile]], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = selectedLogFile;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusIcon = (success: boolean) => {
    return success ? (
      <CheckCircle className="h-5 w-5 text-green-600" />
    ) : (
      <XCircle className="h-5 w-5 text-red-600" />
    );
  };

  const getStatusBadge = (success: boolean) => {
    return (
      <Badge variant={success ? "default" : "destructive"}>
        {success ? "PASSED" : "FAILED"}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">System Tests</h2>
        <p className="text-muted-foreground">
          Run comprehensive tests on the QueueMeIn API endpoints and review results.
        </p>
      </div>

      <Tabs defaultValue="automated-tests" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="automated-tests" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Automated Tests
          </TabsTrigger>
          <TabsTrigger value="data-generator" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Data Generator
          </TabsTrigger>
          <TabsTrigger value="sms-testing" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            SMS Testing
          </TabsTrigger>
          <TabsTrigger value="api-tests" className="flex items-center gap-2">
            <Play className="h-4 w-4" />
            API Tests
          </TabsTrigger>
          <TabsTrigger value="debugging" className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Debugging
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Logs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="automated-tests" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Automated Test Suite
              </CardTitle>
              <CardDescription>
                Run comprehensive automated tests and view historical results
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3 flex-wrap">
                <Button onClick={() => runAutomatedTest('health_check')} disabled={isRunningAutomatedTest}>
                  {isRunningAutomatedTest ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                  Health Check (10 tests)
                </Button>
                <Button onClick={() => runAutomatedTest('queue_manager')} disabled={isRunningAutomatedTest} variant="outline">
                  {isRunningAutomatedTest ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                  Queue Manager (15 tests)
                </Button>
                <Button onClick={() => runAutomatedTest('queue_config')} disabled={isRunningAutomatedTest} variant="outline">
                  {isRunningAutomatedTest ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                  Queue Config (13 tests)
                </Button>
                <Button onClick={() => runAutomatedTest('join_endpoints')} disabled={isRunningAutomatedTest} variant="outline">
                  {isRunningAutomatedTest ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                  Join Endpoints (6 tests)
                </Button>
                <Button onClick={() => runAutomatedTest('eventbridge_schedule')} disabled={isRunningAutomatedTest} variant="outline">
                  {isRunningAutomatedTest ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                  EventBridge (1 test)
                </Button>
                <Button onClick={loadTestHistory} disabled={isLoadingHistory} variant="secondary">
                  {isLoadingHistory ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Refresh History
                </Button>
              </div>
              
              {isRunningAutomatedTest && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-blue-50 rounded">
                  <Clock className="h-4 w-4" />
                  Running tests... This may take 1-2 minutes. Results will appear below.
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Test History</CardTitle>
              <CardDescription>
                View past test results and detailed logs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {automatedTestHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No test history available. Run a test or click "Refresh History" to load results.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid gap-3">
                    {automatedTestHistory.map((result, idx) => (
                      <div 
                        key={idx} 
                        className={`border rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition ${
                          selectedTestResult === result ? 'border-blue-500 bg-blue-50' : ''
                        }`}
                        onClick={() => setSelectedTestResult(result)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {result.passed === result.total_tests ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-600" />
                            )}
                            <div>
                              <div className="font-medium">{result.test_type.replace(/_/g, ' ').toUpperCase()}</div>
                              <div className="text-sm text-gray-600">
                                {new Date(result.timestamp).toLocaleString()} • {result.environment}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-sm font-medium">
                                {result.passed}/{result.total_tests} passed
                              </div>
                              <div className="text-xs text-gray-600">
                                {result.duration_seconds.toFixed(1)}s
                              </div>
                            </div>
                            <Badge variant={result.passed === result.total_tests ? "default" : "destructive"}>
                              {result.passed === result.total_tests ? "PASSED" : "FAILED"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {selectedTestResult && (
                    <div className="mt-6 border-t pt-6">
                      <h4 className="font-medium mb-3">Test Details</h4>
                      <Textarea
                        value={JSON.stringify(selectedTestResult, null, 2)}
                        readOnly
                        className="font-mono text-sm min-h-[400px]"
                      />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="data-generator">
          <SampleDataGenerator />
        </TabsContent>

        <TabsContent value="sms-testing" className="space-y-6">

        {/* SMS Chat Testing */}
        <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            No-Show Requeue Debugging
          </CardTitle>
          <CardDescription>
            Debug no-show requeue system - view schedules and Lambda logs
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <Button
              onClick={() => {
                const schedulerUrl = 'https://us-east-1.console.aws.amazon.com/scheduler/home?region=us-east-1#schedules';
                window.open(schedulerUrl, '_blank');
              }}
              variant="outline"
            >
              View Active Schedules
            </Button>
            <Button
              onClick={() => {
                const logsUrl = 'https://us-east-1.console.aws.amazon.com/cloudwatch/home?region=us-east-1#logsV2:log-groups/log-group/$252Faws$252Flambda$252FQmiApiStack-AdminQueuesLambda';
                window.open(logsUrl, '_blank');
              }}
              variant="outline"
            >
              View Queue Lambda Logs
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            When marking someone as no-show, check the Queue Lambda logs for schedule creation errors, then verify if schedules appear in EventBridge.
          </p>
        </CardContent>
      </Card>

        {/* Test Queue Entries */}
        <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Test Queue Entries
          </CardTitle>
          <CardDescription>
            Add test customers to queues for testing purposes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium">Select Queue:</label>
              <Select value={selectedQueue} onValueChange={setSelectedQueue}>
                <SelectTrigger>
                  <SelectValue placeholder={queues.length === 0 ? "No queues available" : "Select a queue"} />
                </SelectTrigger>
                <SelectContent>
                  {queues.map((queue: any) => (
                    <SelectItem key={`${queue.location_id}/${queue.service_id}`} value={`${queue.location_id}/${queue.service_id}`}>
                      {queue.location_id} / {queue.service_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-gray-500 mt-1">
                Tenant: {activeTenantId || 'None'} | Queues found: {queues.length}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Number of Entries:</label>
              <Input
                type="number"
                min="1"
                max="20"
                value={testCount}
                onChange={(e) => setTestCount(parseInt(e.target.value) || 1)}
              />
            </div>
            
            <div className="flex items-end">
              <Button
                onClick={addTestEntries}
                disabled={!selectedQueue || isAddingEntries}
                className="w-full"
              >
                {isAddingEntries ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Adding...</>
                ) : (
                  <><Users className="h-4 w-4 mr-2" />Add Test Entries</>
                )}
              </Button>
            </div>
          </div>
          
          {queueConfig?.questions && queueConfig.questions.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-sm mb-2">Queue Questions (will be auto-filled):</h4>
              <div className="text-xs text-gray-600 space-y-1">
                {queueConfig.questions.map((q: any) => (
                  <div key={q.id}>
                    <strong>{q.label}</strong> ({q.type})
                    {q.options && ` - Options: ${q.options.join(', ')}`}
                    {q.min && q.max && ` - Range: ${q.min}-${q.max}`}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {entryLog && (
            <div>
              <h4 className="font-medium mb-2">Entry Log:</h4>
              <Textarea
                value={entryLog}
                readOnly
                className="font-mono text-sm min-h-[150px]"
              />
            </div>
          )}
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="api-tests" className="space-y-6">
        {/* Test Controls */}
        <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            SMS Chat Simulator
          </CardTitle>
          <CardDescription>
            Test SMS responses by simulating text message conversations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Test Phone Number:</label>
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+14155551234"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Selected Tenant:</label>
              <Input
                value={activeTenantId || 'No tenant selected'}
                readOnly
                className="bg-gray-50"
              />
            </div>
          </div>
          
          {/* Chat Window */}
          <div className="border rounded-lg h-64 overflow-y-auto p-4 bg-gray-50">
            {smsMessages.length === 0 ? (
              <div className="text-center text-gray-500 mt-20">
                <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No messages yet. Send a test message to start the conversation.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {smsMessages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      msg.direction === 'out' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-white border shadow-sm'
                    }`}>
                      <p className="text-sm">{msg.message}</p>
                      <p className={`text-xs mt-1 ${
                        msg.direction === 'out' ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {msg.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Message Input */}
          <div className="flex gap-2">
            <Input
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              placeholder="Type your SMS message..."
              onKeyPress={(e) => e.key === 'Enter' && !isSendingSms && sendSmsMessage()}
            />
            <Button onClick={sendSmsMessage} disabled={isSendingSms || !currentMessage.trim()}>
              {isSendingSms ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
            <Button onClick={clearSmsChat} variant="outline" size="sm">
              Clear
            </Button>
          </div>
          
          <div className="text-xs text-gray-600">
            <p><strong>How it works:</strong> Messages are sent to the SMS inbound handler, processed through AI (if enabled), and responses are returned just like real SMS.</p>
            <p><strong>Test commands:</strong> Try "STATUS", "What's my position?", "Push me back", "LEAVE", "SWITCH", etc.</p>
          </div>
        </CardContent>
      </Card>

        {/* AI Testing */}
        <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            AI Features Testing
          </CardTitle>
          <CardDescription>
            Test AI assistant capabilities and natural language processing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <Button onClick={testAiFeatures} disabled={isTestingAi}>
              {isTestingAi ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Testing AI...</>
              ) : (
                <><Bot className="h-4 w-4 mr-2" />Test AI Features</>
              )}
            </Button>
          </div>
          
          {aiTestResults && (
            <div className="space-y-3">
              <h4 className="font-medium">AI Test Results:</h4>
              <div className="space-y-2">
                {aiTestResults.map((result: any, index: number) => (
                  <div key={index} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{result.message}</span>
                      <div className="flex items-center gap-2">
                        {result.aiProcessed && (
                          <Badge variant="default" className="text-xs">AI</Badge>
                        )}
                        {result.intent && (
                          <Badge variant="outline" className="text-xs">{result.intent}</Badge>
                        )}
                        {getStatusIcon(result.success)}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">{result.response}</p>
                    {result.confidence && (
                      <p className="text-xs text-gray-500 mt-1">Confidence: {result.confidence}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="debugging" className="space-y-6">
        {/* Debug DynamoDB Records */}
        <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Debug DynamoDB Records
          </CardTitle>
          <CardDescription>
            Query and analyze DynamoDB records directly to debug journey count issues
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium">Tenant ID:</label>
              <Input
                value={activeTenantId || ''}
                readOnly
                className="bg-gray-50"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Date From:</label>
              <Input
                type="date"
                defaultValue={new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]}
                id="debug-date-from"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Date To:</label>
              <Input
                type="date"
                defaultValue={new Date().toISOString().split('T')[0]}
                id="debug-date-to"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={async () => {
                if (!activeTenantId) return;
                
                const dateFrom = (document.getElementById('debug-date-from') as HTMLInputElement)?.value;
                const dateTo = (document.getElementById('debug-date-to') as HTMLInputElement)?.value;
                
                try {
                  const params = new URLSearchParams({
                    tenant_id: activeTenantId,
                    ...(dateFrom && { date_from: dateFrom }),
                    ...(dateTo && { date_to: dateTo })
                  });
                  
                  const response = await fetch(`${API_BASE_URL}/admin/debug/records?${params}`, {
                    headers: {
                      'Authorization': `Bearer ${cognitoAuth.getIdToken()}`
                    }
                  });
                  
                  const result = await response.json();
                  
                  // Display results in logs
                  const fileName = `debug_records_${new Date().toISOString().slice(0,19).replace(/:/g,'-')}.json`;
                  setLogs(prev => ({
                    ...prev,
                    [fileName]: JSON.stringify(result, null, 2)
                  }));
                  setSelectedLogFile(fileName);
                  
                } catch (error) {
                  console.error('Failed to debug records:', error);
                  const fileName = `debug_error_${new Date().toISOString().slice(0,19).replace(/:/g,'-')}.txt`;
                  setLogs(prev => ({
                    ...prev,
                    [fileName]: `Error debugging records: ${error}`
                  }));
                  setSelectedLogFile(fileName);
                }
              }}
              className="flex items-center gap-2"
            >
              <Database className="h-4 w-4" />
              Query Records
            </Button>
            
            <Button
              onClick={() => {
                const dynamoUrl = 'https://us-east-1.console.aws.amazon.com/dynamodbv2/home?region=us-east-1#item-explorer?table=qmi_queue_entries';
                window.open(dynamoUrl, '_blank');
              }}
              variant="outline"
            >
              Open DynamoDB Console
            </Button>
          </div>
          
          <div className="text-xs text-gray-600 space-y-1">
            <p><strong>What this does:</strong> Queries DynamoDB directly to analyze record counts, requeue chains, and data integrity.</p>
            <p><strong>Journey Analysis:</strong> Shows total records, status breakdown, requeue chains (moved_from), and sample records.</p>
            <p><strong>Debugging Tips:</strong> Look for duplicate records, missing requeue links, or status inconsistencies.</p>
          </div>
        </CardContent>
      </Card>

        {/* EventBridge Schedules & Logs */}
        <Card>
        <CardHeader>
          <CardTitle>Test Execution</CardTitle>
          <CardDescription>
            Run different test suites to validate system functionality
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 flex-wrap">
            <Button 
              onClick={() => runTests('simple')} 
              disabled={isRunning}
              className="flex items-center gap-2"
            >
              {isRunning ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Simple Tests
            </Button>
            <Button 
              onClick={() => runTests('auth')} 
              disabled={isRunning}
              variant="outline"
              className="flex items-center gap-2"
            >
              {isRunning ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Auth Tests
            </Button>
            <Button 
              onClick={() => runTests('tenant')} 
              disabled={isRunning}
              variant="outline"
              className="flex items-center gap-2"
            >
              {isRunning ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Tenant Tests
            </Button>
            <Button 
              onClick={() => runTests('queue')} 
              disabled={isRunning}
              variant="outline"
              className="flex items-center gap-2"
            >
              {isRunning ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Queue Tests
            </Button>
            <Button 
              onClick={() => runTests('reports')} 
              disabled={isRunning}
              variant="outline"
              className="flex items-center gap-2"
            >
              {isRunning ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Reports Tests
            </Button>
            <Button 
              onClick={() => runTests('lex')} 
              disabled={isRunning}
              variant="outline"
              className="flex items-center gap-2"
            >
              {isRunning ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Lex Tests
            </Button>
            <Button 
              onClick={() => runTests('all')} 
              disabled={isRunning}
              variant="outline"
              className="flex items-center gap-2"
            >
              {isRunning ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              All Tests
            </Button>
          </div>
          
          {isRunning && (
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Tests are running... This may take up to 5 minutes.
            </div>
          )}
        </CardContent>
      </Card>

        {/* Test Results */}
        {testResults && (
          <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {getStatusIcon(testResults.success)}
                  Test Results: {testResults.test_type}
                </CardTitle>
                <CardDescription>
                  Script: {testResults.script} | Exit Code: {testResults.exit_code}
                </CardDescription>
              </div>
              {getStatusBadge(testResults.success)}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {testResults.stdout && (
              <div>
                <h4 className="font-medium mb-2">Output:</h4>
                <Textarea
                  value={testResults.stdout}
                  readOnly
                  className="font-mono text-sm min-h-[200px]"
                />
              </div>
            )}
            
            {testResults.stderr && (
              <div>
                <h4 className="font-medium mb-2 text-red-600">Errors:</h4>
                <Textarea
                  value={testResults.stderr}
                  readOnly
                  className="font-mono text-sm min-h-[100px] border-red-200"
                />
              </div>
            )}
          </CardContent>
          </Card>
        )}

        </TabsContent>

        <TabsContent value="logs" className="space-y-6">
        {/* Test Logs */}
        <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Test Logs</CardTitle>
              <CardDescription>
                View and download historical test results
              </CardDescription>
            </div>
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Button onClick={loadLogs} variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  File Logs
                </Button>
                <Button onClick={loadCloudWatchLogs} variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  CloudWatch
                </Button>
                {selectedLogFile && (
                  <Button onClick={downloadLogs} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                )}
                <Button 
                  onClick={() => cleanupLogs(7)} 
                  variant="outline" 
                  size="sm"
                  disabled={isCleaningUp}
                  className="text-red-600 hover:text-red-700"
                >
                  {isCleaningUp ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : '🗑️'}
                  Clean 7d+
                </Button>
                <Button 
                  onClick={() => cleanupLogs(1)} 
                  variant="outline" 
                  size="sm"
                  disabled={isCleaningUp}
                  className="text-red-600 hover:text-red-700"
                >
                  Clean 1d+
                </Button>
              </div>
              
              <div className="space-y-2">
                <div className="text-sm font-medium">CloudWatch Settings:</div>
                <div className="flex gap-4 items-center text-sm">
                  <div className="flex items-center gap-2">
                    <label htmlFor="cw-minutes">Time Range:</label>
                    <select 
                      id="cw-minutes"
                      value={cloudWatchMinutes} 
                      onChange={(e) => setCloudWatchMinutes(Number(e.target.value))}
                      className="px-2 py-1 border rounded text-sm"
                    >
                      <option value={5}>5 minutes</option>
                      <option value={10}>10 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>1 hour</option>
                      <option value={180}>3 hours</option>
                      <option value={720}>12 hours</option>
                      <option value={1440}>24 hours</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <label htmlFor="cw-limit">Max Events:</label>
                    <select 
                      id="cw-limit"
                      value={cloudWatchLimit} 
                      onChange={(e) => setCloudWatchLimit(Number(e.target.value))}
                      className="px-2 py-1 border rounded text-sm"
                    >
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                      <option value={500}>500</option>
                      <option value={1000}>1000</option>
                    </select>
                  </div>
                </div>
                
                <div className="text-sm font-medium">File Logs Settings:</div>
                <div className="flex gap-4 items-center text-sm">
                  <div className="flex items-center gap-2">
                    <label htmlFor="file-minutes">Time Range:</label>
                    <select 
                      id="file-minutes"
                      value={fileLogMinutes} 
                      onChange={(e) => setFileLogMinutes(Number(e.target.value))}
                      className="px-2 py-1 border rounded text-sm"
                    >
                      <option value={60}>1 hour</option>
                      <option value={180}>3 hours</option>
                      <option value={720}>12 hours</option>
                      <option value={1440}>24 hours</option>
                      <option value={4320}>3 days</option>
                      <option value={10080}>7 days</option>
                      <option value={43200}>30 days</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <label htmlFor="file-limit">Max Files:</label>
                    <select 
                      id="file-limit"
                      value={fileLogLimit} 
                      onChange={(e) => setFileLogLimit(Number(e.target.value))}
                      className="px-2 py-1 border rounded text-sm"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {Object.keys(logs).length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No test logs available. Click "Refresh Logs" to load available logs.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Select Log File:</label>
                <select 
                  value={selectedLogFile} 
                  onChange={(e) => setSelectedLogFile(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2"
                >
                  <option value="">Select a log file...</option>
                  {Object.keys(logs).map(filename => (
                    <option key={filename} value={filename}>{filename}</option>
                  ))}
                </select>
              </div>
              
              {selectedLogFile && logs[selectedLogFile] && (
                <div>
                  <h4 className="font-medium mb-2">Log Content:</h4>
                  <Textarea
                    value={logs[selectedLogFile]}
                    readOnly
                    className="font-mono text-sm min-h-[400px]"
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
        </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}